import { Button, PlainTooltip } from "@similarweb/ui-components";

import { SuggestedList } from "app/pages/activation/components/Topics/SuggestedList/SuggestedList";
import { SuggestedLoader } from "app/pages/activation/components/Topics/SuggestedLoader/SuggestedLoader";
import { useTopicsContext } from "app/pages/activation/context/Topics/TopicsContext";
import { useTopicsDataContext } from "app/pages/activation/context/Topics/TopicsDataContext";
import { useTopicsModalContext } from "app/pages/activation/context/Topics/TopicsModalContext";
import { useAddTopics } from "app/pages/activation/hooks/topics/modal/useAddTopics";
import { AutocompleteKeywords } from "pro-features/components/AutocompleteKeywords/AutocompleteKeywords";
import { translate } from "app/filters/i18nFilter";
import { getAutocompleteKeyWordsProps, MAX_TOPICS } from "app/pages/activation/components/Topics/Modal/constants";
import { MODAL_TEXTS } from "app/pages/activation/components/Topics/texts";
import { CreationFlow } from "app/pages/activation/components/Topics/types";

import { generateAutomationName } from "../../../../../helpers/generateAutomationName";
import { EmptySuggested } from "../../../EmptySuggested/EmptySuggested";
import {
    StyledBody,
    StyledBodyText,
    StyledCreatedTopicsCounter,
    StyledFooter,
    StyledHeader,
    StyledKeyWordsContainer,
    StyledSubTitleContainer,
    StyledTitle,
} from "../../styles";
import { keywordModalTexts } from "../Advanced/texts";

import { StyledIconButton } from "./styles";

export const AddTopicsFlow = () => {
    const { createdTopicsLength, createdTopics, isLoading } = useTopicsDataContext();

    const { onClose, onAdvanceMenuClick } = useTopicsModalContext();

    const { onSort, activationId, granularity } = useTopicsContext();

    const {
        selectedTopics,
        itemsList,
        isMaxSelected,
        tooltipText,
        isSuggestionLoading,
        isMaxForOneAddSelected,
        selectTopic,
        onKeywordClick,
        onCreate,
        onCancel,
    } = useAddTopics({
        activationId,
        createdTopicsLength,
        createdTopics,
        granularity,
        onClose,
        onSort,
    });

    const isTooltipEnabled = isMaxSelected || !selectedTopics.length || isMaxForOneAddSelected;

    return (
        <>
            <StyledHeader data-automation={generateAutomationName("home", ["topics", "modal", "header"])}>
                <StyledTitle data-automation={generateAutomationName("home", ["topics", "modal", "title"])}>
                    {translate(MODAL_TEXTS.titleAdd)}
                </StyledTitle>
                <StyledIconButton
                    iconName="edit-icon"
                    type="flat"
                    onClick={onAdvanceMenuClick}
                    dataAutomation={generateAutomationName("home", ["topics", "modal", "advance", "button"])}
                >
                    {translate(keywordModalTexts.advancedButtonText)}
                </StyledIconButton>
            </StyledHeader>

            <StyledBody data-automation={generateAutomationName("home", ["topics", "modal", "body"])}>
                <StyledSubTitleContainer>
                    <StyledBodyText data-automation={generateAutomationName("home", ["topics", "modal", "subtitle"])}>
                        {translate(MODAL_TEXTS.subtitle)}
                    </StyledBodyText>

                    <StyledCreatedTopicsCounter
                        data-automation={generateAutomationName("home", ["topics", "modal", "created", "counter"])}
                    >
                        {translate(MODAL_TEXTS.counter, { created: createdTopicsLength, max: MAX_TOPICS })}
                    </StyledCreatedTopicsCounter>
                </StyledSubTitleContainer>

                <AutocompleteKeywords
                    onClick={onKeywordClick}
                    emptyState={{ hideEmptyState: true }}
                    autocompleteProps={getAutocompleteKeyWordsProps(isMaxSelected || isMaxForOneAddSelected)}
                />

                <StyledKeyWordsContainer>
                    {isSuggestionLoading && !itemsList.length ? (
                        <SuggestedLoader />
                    ) : itemsList.length ? (
                        <SuggestedList
                            selectTopic={selectTopic}
                            isMaxForOneAddSelected={isMaxForOneAddSelected}
                            isMaxSelected={isMaxSelected}
                            selectedTopics={selectedTopics}
                            items={itemsList}
                        />
                    ) : (
                        <EmptySuggested />
                    )}
                </StyledKeyWordsContainer>
            </StyledBody>

            <StyledFooter>
                <Button
                    type="flat"
                    onClick={onCancel(CreationFlow.Suggestion)}
                    isDisabled={isLoading}
                    dataAutomation={generateAutomationName("home", [
                        "topics",
                        "modal",
                        "item",
                        "topic",
                        "cancel",
                        "button",
                    ])}
                >
                    {translate(MODAL_TEXTS.buttonCancel)}
                </Button>

                {isTooltipEnabled ? (
                    <PlainTooltip
                        tooltipContent={tooltipText}
                        defaultOpen={isMaxSelected || isMaxForOneAddSelected}
                    >
                        <div>
                            <Button
                                onClick={onCreate}
                                isLoading={isLoading}
                                isDisabled={!selectedTopics.length}
                                dataAutomation={generateAutomationName("home", [
                                    "topics",
                                    "modal",
                                    "item",
                                    "topic",
                                    "confirm",
                                    "button",
                                ])}
                            >
                                {translate(MODAL_TEXTS.buttonConfirm)}
                            </Button>
                        </div>
                    </PlainTooltip>
                ) : (
                    <Button
                        onClick={onCreate}
                        isLoading={isLoading}
                        isDisabled={!selectedTopics.length}
                        dataAutomation={generateAutomationName("home", [
                            "topics",
                            "modal",
                            "item",
                            "topic",
                            "confirm",
                            "button",
                        ])}
                    >
                        {translate(MODAL_TEXTS.buttonConfirm)}
                    </Button>
                )}
            </StyledFooter>
        </>
    );
};

AddTopicsFlow.displayName = "AddTopicsFlow";
